import { useCookies } from '@vueuse/integrations/useCookies'
import { useSessionStore } from '~~/store/session'
import { useTasksStore } from '~~/store/tasks'
import { reportAuthIssue } from '@/utils/authDiagnostics'

// trigger build ###

const PUBLIC_ROUTE_PREFIXES = ['/sign-in', '/sign-up', '/auth', '/invite']
const PUBLIC_ROUTES = new Set(['/blocked', '/onboarding', '/sign-document'])
const SIGN_IN_PATH = '/sign-in'

function isSignInRoute(path) {
  return path === SIGN_IN_PATH || path.startsWith(`${SIGN_IN_PATH}/`)
}

function isPublicRoute(path) {
  if (PUBLIC_ROUTES.has(path)) return true

  return PUBLIC_ROUTE_PREFIXES.some(
    prefix => path === prefix || path.startsWith(`${prefix}/`),
  )
}

export default defineNuxtRouteMiddleware(async (to, from) => {
  const runtimeConfig = useRuntimeConfig()

  const clerkSessionStore = useClerkSessionStore()

  if (to.name === 'sign-out') {
    reportAuthIssue('middleware_sign_out_route_entered', {
      toPath: to.path,
      fromPath: from.path,
      fromName: from.name || null,
    })
    try {
      await clerkSessionStore.signOut()
    }
    catch (error) {
      reportAuthIssue(
        'middleware_sign_out_failed_but_redirecting',
        {
          toPath: to.path,
          fromPath: from.path,
          fromName: from.name || null,
        },
        error,
      )
    }

    if (isSignInRoute(from.path)) {
      return abortNavigation()
    }

    return navigateTo({ path: SIGN_IN_PATH, replace: true })
  }

  if (to.path === '/sign-in/callback') {
    if (clerkSessionStore.needSecondFactor) {
      return navigateTo('/sign-in/2fa')
    }

    return navigateTo(
      `/sign-up?error=${encodeURIComponent(
        'This account doesn\'t exist. Please create a new one.',
      )}`,
    )
  }

  if (to.path === '/') {
    if (runtimeConfig.public.isLocalDevelopment) return

    return navigateTo({
      path: '/dashboard',
      query: to.query.oauth ? { oauth: to.query.oauth } : {},
    })
  }

  if (isPublicRoute(to.path)) return

  const clerkUserStore = useClerkUserStore()

  const userCookie = useCookies([])
  const userCookieValue = userCookie.get('rise-user')
  if (userCookieValue) {
    clerkUserStore.user = userCookieValue
  }
  const clerkHasSession = Boolean(clerkSessionStore.hasSession())
  const serverHasSession = userCookieValue || clerkHasSession

  if (!serverHasSession) {
    reportAuthIssue('middleware_missing_session_redirect_to_sign_in', {
      toPath: to.path,
      fromPath: from.path,
      hasUserCookie: Boolean(userCookieValue),
      hasClerkSession: clerkHasSession,
      clearedSessionFlag: clerkSessionStore.cleared,
    })
    return navigateTo({ path: '/sign-in', query: { redirect: to.path }, replace: true })
  }

  clerkSessionStore.cleared = false

  const sessionStore = useSessionStore()
  const tasksStore = useTasksStore()

  try {
    if (!sessionStore.user) {
      const _me = await sessionStore.me()
      if (!_me) {
        reportAuthIssue('middleware_me_empty_redirect_to_sign_out', {
          toPath: to.path,
          fromPath: from.path,
          hasClerkSession: Boolean(clerkSessionStore.hasSession()),
          hasUserCookie: Boolean(userCookieValue),
        })
        return navigateTo('/sign-in')
      }

      await tasksStore.fetch()
      if (_me?.countryBlacklisted) return navigateTo('/blocked')
    }

    if (to.query.oauth === 'true') {
      return navigateTo(runtimeConfig.public.V2_PUBLIC_URL, {
        external: true,
        replace: true,
      })
    }

    if (sessionStore.needsOnboarding) {
      if (sessionStore.shouldRedirectToV2) {
        const reason = sessionStore.user?.hasV2Invite ? 'pending_invites' : 'v2_onboarding'
        const { redirectToV2Onboarding } = useRedirectToV2Onboarding({ reason })
        return redirectToV2Onboarding()
      }

      await clerkSessionStore.refreshSession()
      const sessionId = clerkSessionStore.sessionId
      if (runtimeConfig.public.isLocalDevelopment) {
        location.href = `http://localhost:3001/onboarding/session?session_id=${sessionId}`
        return
      }

      return await navigateTo(
        `${runtimeConfig.public.ONBOARDING_URL}/onboarding/`,
        {
          external: true,
          replace: true,
        },
      )
    }

    return
  }
  catch (e) {
    reportAuthIssue(
      'middleware_auth_guard_failed_redirect_to_sign_out',
      {
        toPath: to.path,
        fromPath: from.path,
        hasSessionUser: Boolean(sessionStore.user),
        hasClerkSession: Boolean(clerkSessionStore.hasSession()),
      },
      e,
    )
    console.error(e)
    // this.$toastError("Your session was expired, please log in again.");
    // this.$sentryCaptureException(e);
    return navigateTo('/sign-out')
  }
})
